import { useState, useEffect } from 'react'
import { LayoutDashboard, Newspaper, Eye, CreditCard, Bookmark, TrendingUp } from 'lucide-react'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { dashboardApi, newsApi } from '@/services/api'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { motion } from 'framer-motion'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import axios from 'axios'
import { getAuthHeaders } from '@/lib/utils'
import { API_URL } from '@/lib/config'

const CHART_COLORS = [
  'hsl(221, 83%, 53%)',  // Blue
  'hsl(142, 71%, 45%)',  // Green
  'hsl(262, 83%, 58%)',  // Purple
  'hsl(31, 97%, 52%)',   // Orange
  'hsl(339, 82%, 52%)',  // Pink
  'hsl(199, 89%, 48%)',  // Cyan
  'hsl(48, 96%, 53%)',   // Yellow
  'hsl(4, 90%, 58%)',    // Red
  'hsl(280, 67%, 60%)',  // Violet
  'hsl(173, 58%, 39%)',  // Teal
]

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-900">{payload[0].payload.feed_name}</p>
        <p className="text-sm text-gray-600">
          Time: {payload[0].payload.total_min} minutes
        </p>
        <p className="text-sm text-gray-500">
          {payload[0].payload.percent}%
        </p>
      </div>
    )
  }
  return null
}

export function DashboardOverview() {
  const [readData, setReadData] = useState(null)
  const [chartData, setChartData] = useState([])
  const [month, setMonth] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch subscribed feeds count
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await dashboardApi.getSubscribedFeeds()
      return response.data
    }
  })

  // Fetch reading stats
  useEffect(() => {
    const fetchReadData = async () => {
      try {
        const headers = getAuthHeaders()
        const response = await axios.get(
          `${API_URL}/api/v1/users/feeds/read/`,
          headers
        )
        setReadData(response.data)
      } catch (error) {
        console.error('Error fetching read data:', error)
      }
    }

    const fetchChartData = async () => {
      try {
        const headers = getAuthHeaders()
        const response = await axios.get(
          `${API_URL}/api/v1/users/feeds/read/breakdown/`,
          headers
        )
        
        const breakdown = response.data.breakdown || []
        const formattedData = breakdown.map((feed, index) => ({
          feed_name: feed.feed_name || 'Unknown',
          total_min: feed.total_min,
          percent: feed.percent.toFixed(2),
          fill: CHART_COLORS[index % CHART_COLORS.length]
        }))
        
        setChartData(formattedData)
        setMonth(response.data.month)
      } catch (error) {
        console.error('Error fetching chart data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchReadData()
    fetchChartData()
  }, [])

  const totalFeeds = subscription?.feeds?.length || 0
  const totalArticles = readData?.total_article || 0
  const totalMinutes = readData?.total_minutes || 0
  const totalPurchases = readData?.total_purchase || 0
  const totalReadTime = chartData.reduce((sum, item) => sum + item.total_min, 0)

  const stats = [
    {
      title: 'Total Feeds',
      value: totalFeeds,
      description: 'Number of custom feeds in your list',
      icon: Newspaper,
      color: 'text-primary',
      bgColor: 'bg-slate-50'
    },
    {
      title: 'Articles Read',
      value: totalArticles,
      description: `${totalMinutes} minutes total read time`,
      icon: Eye,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Purchases',
      value: totalPurchases,
      description: 'Number of articles you have purchased',
      icon: CreditCard,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100'
    }
  ]

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <LayoutDashboard className="w-8 h-8 text-primary" />
          Dashboard Overview
        </h1>
        <p className="text-gray-600">
          Your activity and reading statistics for this month
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stat.value > 0 ? '+' : ''}{stat.value}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>

      {/* Reading Time Pie Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader className="items-center pb-0">
            <CardTitle>Reading Time Distribution</CardTitle>
            <CardDescription>{month || 'This Month'}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 pb-0 py-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : chartData.length > 0 ? (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ feed_name, percent }) => `${feed_name} (${percent}%)`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="total_min"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Legend */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {chartData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span className="text-sm text-gray-700 truncate">
                        {item.feed_name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Eye className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No reading data yet
                </h3>
                <p className="text-gray-600">
                  Start reading articles to see your reading statistics
                </p>
              </div>
            )}
          </CardContent>
          {chartData.length > 0 && (
            <CardFooter className="flex-col gap-2 text-sm">
              <div className="flex items-center gap-2 font-medium leading-none">
                Total read time {totalReadTime.toFixed(2)} minutes
                <TrendingUp className="h-4 w-4" />
              </div>
              <div className="leading-none text-gray-500">
                Showing total read time based on your feeds this month
              </div>
            </CardFooter>
          )}
        </Card>
      </motion.div>
    </div>
  )
}
